import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Store, Users, ShoppingCart, MessageSquare, Sparkles, Globe, Mail, Phone } from "lucide-react";

type InquiryType = "explore" | "distributor" | "quote" | "support" | "";

const inquiryOptions = [
  {
    id: "explore" as InquiryType,
    title: "Explore Collections",
    description: "Browse our luxury smart toilet series",
    icon: Sparkles,
  },
  {
    id: "distributor" as InquiryType,
    title: "Become a Partner",
    description: "Join our exclusive distribution network",
    icon: Users,
  },
  {
    id: "quote" as InquiryType,
    title: "Get a Quote",
    description: "Request pricing for your project",
    icon: ShoppingCart,
  },
  {
    id: "support" as InquiryType,
    title: "Customer Service",
    description: "Get help with your inquiry",
    icon: MessageSquare,
  },
];

export default function ClientHub() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"welcome" | "form" | "success">("welcome");
  const [inquiryType, setInquiryType] = useState<InquiryType>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    country: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleInquirySelect = (type: InquiryType) => {
    setInquiryType(type);
    setStep("form");
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !inquiryType) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      // Save to database
      const { data: lead, error: dbError } = await supabase
        .from("leads")
        .insert({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          company: formData.company || null,
          location: formData.country || null,
          interest_type: inquiryType,
          message: formData.message || null,
          qualification_score: 75, // Higher score for structured form submissions
          status: "new",
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Send email notifications
      const { error: emailError } = await supabase.functions.invoke("send-lead-notification", {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          country: formData.country,
          inquiryType: inquiryOptions.find(opt => opt.id === inquiryType)?.title || inquiryType,
          message: formData.message,
        },
      });

      if (emailError) {
        if (import.meta.env.DEV) console.error("Email error:", emailError);
        // Don't fail the submission if email fails, just log it
      }

      toast.success("Thank you! Your inquiry has been submitted successfully.");
      setStep("success");
    } catch (error: any) {
      if (import.meta.env.DEV) console.error("Submission error:", error);
      toast.error("Failed to submit. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep("welcome");
    setInquiryType("");
    setFormData({
      name: "",
      company: "",
      country: "",
      email: "",
      phone: "",
      message: "",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-marble">
      <SEO 
        title="Client Hub - Contact Aureum VII Trading"
        description="Connect with Aureum VII Trading for luxury smart toilet solutions, partnership opportunities, and customer support."
      />
      <Navigation />
      
      <main className="container mx-auto px-4 py-16 sm:px-6 lg:px-8">
        {step === "welcome" && (
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-navy-gold bg-clip-text text-transparent px-4">
                Welcome to Aureum VII Trading
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground px-4">
                Where innovation meets sophistication
              </p>
              <p className="text-base sm:text-lg text-muted-foreground mt-2 px-4">
                How can we assist you today?
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 px-4">
              {inquiryOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <Card
                    key={option.id}
                    className="cursor-pointer transition-all duration-300 hover:shadow-gold hover:border-accent/50 hover:scale-105 group bg-white"
                    onClick={() => handleInquirySelect(option.id)}
                  >
                    <CardHeader className="p-6 sm:p-8">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 sm:p-4 rounded-xl bg-gradient-elegant group-hover:bg-gradient-gold transition-all duration-300">
                          <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                        </div>
                        <CardTitle className="text-lg sm:text-xl text-navy">{option.title}</CardTitle>
                      </div>
                      <CardDescription className="text-sm sm:text-base text-muted-foreground">
                        {option.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {step === "form" && (
          <div className="max-w-2xl mx-auto px-4">
            <Card className="bg-white shadow-elegant border-navy/10">
              <CardHeader className="border-b border-navy/10">
                <CardTitle className="flex items-center gap-3 text-xl sm:text-2xl text-navy">
                  {inquiryOptions.find(opt => opt.id === inquiryType)?.icon && (
                    <span className="p-3 rounded-xl bg-gradient-elegant">
                      {(() => {
                        const Icon = inquiryOptions.find(opt => opt.id === inquiryType)!.icon;
                        return <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />;
                      })()}
                    </span>
                  )}
                  {inquiryOptions.find(opt => opt.id === inquiryType)?.title}
                </CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  Please provide your details so we can assist you better
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        placeholder="John Smith"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => handleInputChange("company", e.target.value)}
                        placeholder="Your Company Ltd."
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => handleInputChange("country", e.target.value)}
                      placeholder="United Kingdom"
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          placeholder="john@example.com"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone / WhatsApp</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          placeholder="+44 20 1234 5678"
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Additional Information</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange("message", e.target.value)}
                      placeholder="Tell us more about your requirements..."
                      rows={4}
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetForm}
                      className="flex-1 border-navy/20 text-navy hover:bg-navy/5"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-gradient-elegant hover:bg-gradient-gold text-white shadow-gold"
                    >
                      {isSubmitting ? "Submitting..." : "Submit Inquiry"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {step === "success" && (
          <div className="max-w-2xl mx-auto text-center px-4">
            <Card className="bg-white shadow-elegant border-navy/10">
              <CardContent className="pt-12 pb-12 px-6 sm:px-12">
                <div className="mb-8">
                  <div className="mx-auto w-20 h-20 bg-gradient-gold rounded-full flex items-center justify-center mb-6 shadow-gold">
                    <svg
                      className="w-10 h-10 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-navy-gold bg-clip-text text-transparent">
                    Thank You!
                  </h2>
                  <p className="text-lg sm:text-xl text-navy mb-3">
                    Thank you for contacting Aureum VII Trading.
                  </p>
                  <p className="text-muted-foreground text-base sm:text-lg">
                    Our representative will be in touch with you within 24 hours.
                  </p>
                </div>

                <div className="bg-gradient-marble rounded-xl p-6 sm:p-8 mb-8 border border-accent/20">
                  <p className="text-sm sm:text-base text-muted-foreground mb-2">
                    A confirmation email has been sent to:
                  </p>
                  <p className="font-semibold text-navy text-base sm:text-lg">{formData.email}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={() => navigate("/")} 
                    variant="outline"
                    className="border-navy/20 text-navy hover:bg-navy/5"
                  >
                    Back to Home
                  </Button>
                  <Button 
                    onClick={resetForm}
                    className="bg-gradient-elegant hover:bg-gradient-gold text-white shadow-gold"
                  >
                    Submit Another Inquiry
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}